import PropTypes from 'prop-types'

const productTilePropTypes = {
    /**
     * The product search hit that will be represented in this
     * component.
     */
    product: PropTypes.shape({
        currency: PropTypes.string,
        image: PropTypes.shape({
            alt: PropTypes.string,
            disBaseLink: PropTypes.string,
            link: PropTypes.string
        }),
        imageGroups: PropTypes.array,
        price: PropTypes.number,
        priceRanges: PropTypes.array,
        tieredPrices: PropTypes.array,
        // `name` is present and localized when `product` is provided by a RecommendedProducts component
        // (from Shopper Products `getProducts` endpoint), but is not present when `product` is
        // provided by a ProductList component.
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-products?meta=getProducts
        name: PropTypes.string,
        // `productName` is localized when provided by a ProductList component (from Shopper Search
        // `productSearch` endpoint), but is NOT localized when provided by a RecommendedProducts
        // component (from Einstein Recommendations `getRecommendations` endpoint).
        // See: https://developer.salesforce.com/docs/commerce/commerce-api/references/shopper-search?meta=productSearch
        // See: https://developer.salesforce.com/docs/commerce/einstein-api/references/einstein-api-quick-start-guide?meta=getRecommendations
        // Note: Einstein recommendation responses use camelCase property names here.
        productName: PropTypes.string,
        productId: PropTypes.string,
        id: PropTypes.string,
        productPromotions: PropTypes.array,
        representedProduct: PropTypes.object,
        hitType: PropTypes.string,
        variationAttributes: PropTypes.array,
        variants: PropTypes.array,
        variationGroups: PropTypes.array,
        productType: PropTypes.object,
        type: PropTypes.shape({
            set: PropTypes.bool,
            bundle: PropTypes.bool,
            item: PropTypes.bool
        }),
        c_tilesSwatches: PropTypes.shape({
            count: PropTypes.number,
            swatchURL: PropTypes.string
        })
    }),
    /**
     * Enable adding/removing product as a favourite.
     * Use case: wishlist.
     */
    enableFavourite: PropTypes.bool,
    /**
     * Keep the wishlist icon visible but prevent any interaction with it.
     * Use case: shared (read-only) wishlist page where the viewer should not
     * be able to add/remove products from their own wishlist.
     */
    disableFavouriteInteraction: PropTypes.bool,
    /**
     * Display the product as a favourite.
     */
    isFavourite: PropTypes.bool,

    /**
     * Display the product as a favourite.
     */
    showSwatches: PropTypes.bool,
    /**
     * Callback function to be invoked when the user
     * interacts with favourite icon/button.
     */
    onFavouriteToggle: PropTypes.func,
    /**
     * The `viewType` of the image component. This defaults to 'large'.
     */
    imageViewType: PropTypes.string,
    /**
     * When displaying a master/variant product, this value represents the variation attribute that is displayed
     * as a swatch below the main image. The default for this property is `color`.
     */
    selectableAttributeId: PropTypes.string,
    dynamicImageProps: PropTypes.object,
    videoProps: PropTypes.shape({
        preload: PropTypes.oneOf(['none', 'metadata', 'auto'])
    }),
    /**
     * Details of badge labels and the corresponding product custom properties that enable badges.
     */
    badgeDetails: PropTypes.array,
    /**
     * Determines whether to display a skeleton over personalizable data (e.g., pricing and promotions) during data refresh.
     */
    isRefreshingData: PropTypes.bool,
    /**
     * PLP-only: true while the phase-2 promotion enrichment fetch hasn't resolved yet for
     * this render pass, so the price shows a skeleton instead of the phase-1 (pre-promotion,
     * possibly wrong) price.
     */
    isPriceLoading: PropTypes.bool,
    setQuickViewProduct: PropTypes.func,
    setMobileQuickViewOpen: PropTypes.func,
    setQuickViewWishlistInfo: PropTypes.func,
    enableWishlistCount: PropTypes.bool,
    showVideo: PropTypes.bool,
    showDetails: PropTypes.bool,
    showProductLikes: PropTypes.bool,
    showQV: PropTypes.bool,
    isShopTheLook: PropTypes.bool,
    isOOS: PropTypes.bool,
    isWishlistPage: PropTypes.bool,
    /**
     * Keeps the Quick View drawer available for a selected wishlist variant.
     * Used by the Cart wishlist carousel, whose plus action must open the drawer.
     */
    allowQuickView: PropTypes.bool,
    /** Hides the saved size below tiles rendered by the Cart wishlist carousel. */
    hideSize: PropTypes.bool,
    /** Hides the saved length below wishlist tiles. */
    hideLength: PropTypes.bool,
    disableImageScroll: PropTypes.bool,
    showScrollHint: PropTypes.bool,
    /**
     * GDI carousel-driven sx fragments — one entry per subcomponent. Each is
     * an Emotion sx object combining a (possibly responsive) `display` value
     * with an optional `@media (hover: hover)` → `[data-product-tile="true"]:hover &`
     * override sourced from the WebDesktopHover view-type entry. Spread on
     * the corresponding subcomponent via `sx={...}`. Default undefined → no
     * override → existing behavior is preserved.
     */
    responsiveDisplay: PropTypes.shape({
        productName: PropTypes.object,
        productPrice: PropTypes.object,
        wishlistIcon: PropTypes.object,
        onImageBadge: PropTypes.object,
        productTags: PropTypes.object,
        colorSwatches: PropTypes.object,
        quickActionsDisplay: PropTypes.object
    }),
    /**
     * GDI carousel-forced tile mode override. String or Chakra responsive
     * object of 'quick-view' | 'quick-add'. Default undefined → use
     * useProductTileMode's screensize-based result (today's behavior).
     */
    forceTileMode: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onClick: PropTypes.func,
    onQuickViewClose: PropTypes.func,
    isLayout: PropTypes.bool,
    /**
     * CMS styling object for product title (extracted to prevent DOM warnings)
     */
    productTitleStyle: PropTypes.object,
    /**
     * CMS styling object for product price (extracted to prevent DOM warnings)
     */
    productPriceStyle: PropTypes.object
}

export default productTilePropTypes
